import React from 'react';

import { AppErrorFallback } from './app-error-fallback';
import { reloadOnceForChunkLoadError } from './chunk-load-recovery';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  resetKey: string;
}

interface AppErrorBoundaryState {
  hasRenderError: boolean;
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasRenderError: true };
  }

  constructor(props: AppErrorBoundaryProps) {
    super(props);

    this.state = { hasRenderError: false };
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
    const { resetKey } = this.props;
    const { hasRenderError } = this.state;

    // Reset only from the error state: a resetKey change while healthy must
    // never disturb (let alone remount) the children.
    if (hasRenderError && resetKey !== prevProps.resetKey) {
      this.setState({ hasRenderError: false });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (reloadOnceForChunkLoadError(error)) {
      return;
    }

    // Seam for a future error-reporting vendor.
    // eslint-disable-next-line no-console
    console.error('[App] render crash', error, info.componentStack);
  }

  render(): React.ReactNode {
    const { children } = this.props;
    const { hasRenderError } = this.state;

    if (hasRenderError) {
      return <AppErrorFallback />;
    }

    return children;
  }
}
